import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Globe } from "lucide-react";
import { cn } from "../../lib/utils";
import { useTranslation, type LangCode } from "../../i18n";

interface LanguageSwitcherProps {
  /** Render light-on-dark (for the colored header) vs dark-on-light. */
  onDark?: boolean;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ onDark = true }) => {
  const { lang, setLang, languages, meta, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("lang.select")}
        title={t("lang.select")}
        className={cn(
          "flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-semibold transition-colors min-h-[40px]",
          onDark
            ? "text-white hover:bg-white/10"
            : "text-gray-700 hover:bg-gray-100 border border-gray-200"
        )}
      >
        <Globe className="h-4 w-4 opacity-80" />
        <span className="text-base leading-none">{meta.flag}</span>
        <span className="hidden sm:inline uppercase tracking-wide">{meta.code}</span>
        <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div
          className="absolute end-0 z-[1200] mt-2 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-[#33334d] dark:bg-[#1f1f3a]"
          role="listbox"
        >
          <div className="border-b border-gray-100 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:border-[#33334d]">
            {t("lang.label")}
          </div>
          {languages.map((l) => {
            const active = l.code === lang;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  setLang(l.code as LangCode);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors",
                  active
                    ? "bg-[#1a2a6c]/5 font-semibold text-[#1a2a6c] dark:bg-white/10 dark:text-white"
                    : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-white/5"
                )}
              >
                <span className="text-lg leading-none">{l.flag}</span>
                <span className="flex-1">{l.nativeName}</span>
                {active && <Check className="h-4 w-4 text-[#c9a227]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
